/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/// <reference path="types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    ArrayExtensions,
    // getOrgUnitLocationsByArea,
    // GetOrgUnitLocationsByAreaInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { /*IStoreInfo,*/ IStoreSelectionStateContext } from '@msdyn365-commerce-modules/bopis-utilities';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { IModuleProps, NodeTag, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as _ from 'lodash';

import { IEveMapData } from './eve-map.data';
import { IEveMapProps, IPushpinOptionsData, searchRadiusUnit as SearchRadiusUnit } from './eve-map.props.autogenerated';
// import getDealerDetails, { GetDealerDetailsInput } from '../../actions/get-dealer-details.action';
import getDealerLocations, { GetDealerLocationsInput } from '../../actions/get-dealer-locations.action';
import { IDealerEntity } from '../../actions/DataServiceEntities.g';
import { getDealerDetailsAsync /*, getDealerLocationsAsync*/ } from '../../actions/DataActionExtension.g';
import {
    DealerSelectorWaiting,
    DealerSelectorEmptyMessage,
    DealerSelectorFoundLocationsMessage,
    DealerLocatorLocationLines,
    DealerLocatorSearchForm
} from './components';

/**
 * Header Interface.
 */
export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}

/**
 * MapViewProps Interface.
 */
export interface IMapViewProps extends IEveMapProps<IEveMapData> {
    ModuleProps: IModuleProps;
    Header: IHeader;
    MapProps: INodeProps;
    Map: Microsoft.Maps.Map | undefined;
    locationsMessage?: React.ReactNode;
    search?: React.ReactNode;
    spinner?: React.ReactNode;
    locationsList?: React.ReactNode;
    noLocationsMessage?: React.ReactNode;
    dealerResultContainerProps?: INodeProps;
    state: IEveMapState;
}

/**
 * EveMap State Interface.
 */
export interface IEveMapState {
    searchTerm?: string;
    isSearchInProgress: boolean;
    distanceResults: IDistanceResults;
    mapSessionId: string | undefined;
    dealerLocations: IFullOrgUnitAvailability[] | undefined;
    newDealerLocations: IDealerLocations[] | undefined;
}

export interface ICoordinates {
    latitude: number;
    longitude: number;
}

export interface IDistanceMatrixResult {
    destinationIndex: number;
    originIndex: number;
    totalWalkDuration: number;
    // travelDistance arrives in kilometers
    travelDistance: number;
    travelDuration: number;
}

export interface IDistanceResults {
    originCoords: ICoordinates;
    resultsCoords: IDistanceMatrixResult[];
}

export interface IDealerLocation {
    ChannelId?: number;
    Latitude?: number;
    Longitude?: number;
    OrgUnitName?: string;
    OrgUnitNumber?: string;
    Address?: string;
    PhoneNumber?: string;
    Distance?: number;
    ContractCode?: string;
    ContractCompensationRate?: number;
    ContractDescription?: string;
    ContractLaborRate?: number;
    ContractListedUnlisted?: boolean;
    ContractNotes?: string;
    ContractStatus?: boolean;
    ManufacturerCode?: string;
    ContractList?: IDealerEntity[];
    RecId?: number;
}

export interface IDealerLocations {
    OrgUnitAvailability?: {
        OrgUnitLocation?: IDealerLocation;
    };
    OrgUnitLocation?: IDealerLocation;
}

// xoxo - does this actually need to be here?
export interface IEveMapPropsExtended extends IEveMapProps<IEveMapData> {
    mapData: any;
}

/**
 *
 * Map component.
 * @extends {React.Component<IEveMapProps<IEveMapData>>}
 */
@observer
class Map extends React.PureComponent<IEveMapPropsExtended, IEveMapState> {
    @observable public map: Microsoft.Maps.Map | undefined;

    @observable public selectedDealer: IDealerEntity[] | undefined;

    private autoSuggestManager: Microsoft.Maps.AutosuggestManager | undefined;

    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    private readonly kmToMiValue: number = 1.609;

    private readonly defaultLookUpRadius: number = 200;

    public constructor(props: IEveMapPropsExtended) {
        super(props);
        this.state = {
            searchTerm: '',
            isSearchInProgress: true,
            distanceResults: { originCoords: { latitude: 0, longitude: 0 }, resultsCoords: [] },
            dealerLocations: [],
            newDealerLocations: [],
            mapSessionId: undefined
        };
    }

    public componentDidMount(): void {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled: isAutoSuggestionEnabled }
            // data: {
            //     storeLocations: { result: storeLocations }
            // }
        } = this.props;

        // // Adding Timeout to make sure it loads the data based upon the preferred store.
        setTimeout(() => {
            this._getAndUpdateStoreLocation();
        }, 0);

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.context?.id,
            async () => {
                const context = this.props.data.storeSelectorStateManager.result?.context;
                if (!context || context.id === this.props.id) {
                    await this._searchTermChanged('');
                    this._getAndUpdateStoreLocation();
                    this._updateMap();
                }
            }
        );

        // if (storeLocations) {
        //     console.log(storeLocations);
        //     this.setState({ newDealerLocations: [...storeLocations] });
        //     // if (this.state.newDealerLocations) {
        //     void this._setOrgUnitStoreInformation(storeLocations);
        //     // }
        // }

        this.map?.setOptions({ navigationBarMode: Microsoft.Maps.NavigationBarMode.minified });
        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }
        if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
            this._initMap();
            this._updateMap();
        }

        when(
            () => !!this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded,
            () => {
                this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded(true);
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.loadMapApi,
            () => {
                this.props.data.storeSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: this.props.context.actionContext.requestContext.locale,
                    market: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode
                });
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
            () => {
                this._initMap();
            }
        );

        reaction(
            () => {
                return [
                    this.props.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
                    this.props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
                    this.map
                ];
            },
            () => {
                this._updateMap();
            }
        );

        if (isAutoSuggestionEnabled) {
            when(
                () => {
                    const isMapApiLoaded = !!this.props.data.storeSelectorStateManager.result?.isMapApiLoaded;
                    const isDialogOpen = !!this.props.data.storeSelectorStateManager.result?.isDialogOpen;
                    const isSameContext = this.props.data.storeSelectorStateManager.result?.context?.id === this.props.id;
                    return isMapApiLoaded && isDialogOpen && isSameContext;
                },
                () => {
                    setTimeout(async () => {
                        await this._attachMapAutoSuggest();
                    }, 0);
                }
            );
        }
    }

    public shouldComponentUpdate(): boolean {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        if (context && context.id === this.props.id) {
            return true;
        }
        return false;
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, className, lookupRadius, searchRadiusUnit },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            resources
        } = this.props;
        const { newDealerLocations } = this.state;
        const shouldDisplayMap = storeSelectorStateManager?.listMapViewState.displayMap;
        const preferredDistanceUnit = searchRadiusUnit === SearchRadiusUnit.km ? resources.kilometersShortText : resources.milesShortText;
        const shouldDisplayList = storeSelectorStateManager?.listMapViewState?.displayList;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;

        // let dealerLocationList: IDealerLocations[] = newDealerLocations || [];
        // const unsortedDealerList = [...dealerLocationList];
        // dealerLocationList = this._sortStores(unsortedDealerList);
        // console.log('sorted');
        // console.log(dealerLocationList);
        // const paginatedLocationsList = newDealerLocations?.slice(0, this.props.config.pageLength);

        // const pagination = (
        //     <EveLinePagination
        //         nextPage={nextPage}
        //         prevPage={prevPage}
        //         changePage={changePage}
        //         currentPage={paginationPage}
        //         numPages={props.cartlines ? Math.ceil(props.cartlines.length / (pageLength)) : 0}
        //         prev={props.resources.paginationPrev}
        //         next={props.resources.paginationNext}
        //     />);

        const viewProps: IMapViewProps = {
            ...this.props,
            state: this.state,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-map', { show: shouldDisplayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-map__header' },
                heading: heading?.text && (
                    <Msdyn365.Text
                        className='ms-map__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            dealerResultContainerProps: {
                tag: 'div' as NodeTag,
                className: 'ms-store-select__dealer_container'
            },
            spinner: <DealerSelectorWaiting />,
            noLocationsMessage: <DealerSelectorEmptyMessage resources={resources} />,
            locationsMessage: (
                <DealerSelectorFoundLocationsMessage
                    resources={resources}
                    locations={newDealerLocations}
                    // locations={dealerLocationList}
                    searchRadius={lookupRadius || this.defaultLookUpRadius}
                    preferredDistanceUnit={preferredDistanceUnit}
                />
            ),
            locationsList:
                // ArrayExtensions.hasElements(dealerLocationList) && dealerLocationList[0].OrgUnitLocation ? (
                //     <DealerLocatorLocationLines
                //         locations={dealerLocationList}
                //         resources={resources}
                //         preferredDistanceUnit={searchRadiusUnit}
                //         selectedStoreLocationId={selectedStoreLocationId}
                //         onClick={this.handleClickEvent}
                //         displayList={shouldDisplayList}
                //     />
                ArrayExtensions.hasElements(newDealerLocations) && newDealerLocations[0].OrgUnitLocation ? (
                    <DealerLocatorLocationLines
                        locations={newDealerLocations}
                        // locations={paginatedLocationsList}
                        resources={resources}
                        preferredDistanceUnit={searchRadiusUnit}
                        selectedStoreLocationId={selectedStoreLocationId}
                        onClick={this.handleClickEvent}
                        displayList={shouldDisplayList}
                    />
                ) : (
                    undefined
                ),
            search: (
                <DealerLocatorSearchForm
                    value={this.state.searchTerm || ''}
                    resources={resources}
                    performSearch={this._performSearch}
                    searchTermChanged={this._searchTermChanged}
                    id={this.props.id}
                />
            ),
            MapProps: {
                tag: 'div',
                className: 'ms-map__body',
                ref: this.mapRef
            },
            Map: this.map
        };
        if (newDealerLocations) {
            return this.props.renderView(viewProps) as React.ReactElement;
        }
        // if (this.state.dealerLocations) {
        //     return this.props.renderView(viewProps) as React.ReactElement;
        // }
        return <></>;
    }

    /**
     * Initialize Map AutoSuggest.
     */
    private readonly _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            config: { autoSuggestOptions },
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded && !this.autoSuggestManager) {
            Microsoft.Maps.loadModule('Microsoft.Maps.AutoSuggest', {
                /**
                 * Callback for the autosuggest API.
                 */
                callback: () => {
                    const options = { ...autoSuggestOptions, countryCode: channel?.ChannelCountryRegionISOCode };
                    this.autoSuggestManager = new Microsoft.Maps.AutosuggestManager(options);
                    this.autoSuggestManager.attachAutosuggest(
                        `#ms-store-select__search-box_${this.props.id}`,
                        `#ms-store-select__search-box-container_${this.props.id}`,
                        this._onSuggestionSelected
                    );
                },

                /**
                 * Error call back for autosuggest API.
                 */
                errorCallback: () => {
                    if (telemetry) {
                        telemetry.debug('Unable to attach map auto suggest.');
                    }
                },
                credentials: channel?.BingMapsApiKey
            });
        }
    };

    /**
     * On Selecting Location Suggestion.
     * @param result - Map Suggestions List.
     */
    private readonly _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        const context = this.props.data.storeSelectorStateManager.result?.context;
        if (context && context.id === this.props.id) {
            await this._searchTermChanged(result.formattedSuggestion);
            await this._performSearch(result.formattedSuggestion);
        }
    };

    /**
     * Method to be called updating the store location.
     * @param shouldShowAllStores - Flag to show all stores.
     */
    private readonly _getAndUpdateStoreLocation = (shouldShowAllStores?: boolean): void => {
        // const {
        //     // config: { style },
        //     data: {
        //         storeSelectorStateManager: { result: storeSelectorStateManager }
        //     }
        // } = this.props;

        // eslint-disable-next-line react/no-unused-state -- State is used in the view.
        this.setState({ isSearchInProgress: true });
        if (navigator?.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async position => {
                    // eslint-disable-next-line react/no-unused-state -- This state is used in the views.
                    this.setState({ isSearchInProgress: true });
                    await this._updateLocations(position.coords.latitude, position.coords.longitude);

                    // eslint-disable-next-line react/no-unused-state -- State is used in the view.
                    this.setState({ isSearchInProgress: false });
                },
                async error => {
                    // If the location setting is turned off
                    if (error.PERMISSION_DENIED) {
                        await this._searchTermChanged('');
                        await this._updateLocations(0, 0, 0);
                    }

                    // eslint-disable-next-line react/no-unused-state -- State is used in the view.
                    this.setState({ isSearchInProgress: false });
                },
                { timeout: 5000 }
            );
        } else {
            // eslint-disable-next-line react/no-unused-state -- State is used in the view.
            this.setState({ isSearchInProgress: false });
        }
    };

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Initialize the map.
     */
    private readonly _initMap = () => {
        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.map = new Microsoft.Maps.Map(this.mapRef.current as HTMLElement, {
                credentials: channel?.BingMapsApiKey,
                pushpinAccessible: true
            });
            Microsoft.Maps.Events.addHandler(this.map, 'click', async () => {
                await storeSelectorStateManager.setSelectedStoreLocationId(undefined);
            });
            this.map.getCredentials((credentials: string) => {
                // TODO: Need to figure out why using map session id in place of api key does not work.
                this.setState({ mapSessionId: credentials });
            });
            this._updateMap();
        }
    };

    /**
     * Update the map.
     */
    private readonly _updateMap = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        if (this.map && orgUnitStoreInformation) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.entities.clear();
            // const pushpins: Microsoft.Maps.IPrimitive[] = [];
            const pushpins: Microsoft.Maps.Pushpin[] = [];

            const storeLocationList = orgUnitStoreInformation.filter(store => store.OrgUnitLocation !== undefined);

            for (const [index, unitStoreInfo] of storeLocationList.entries()) {
                const storeLocation = unitStoreInfo.OrgUnitLocation;

                if (storeLocation?.Latitude && storeLocation.Longitude) {
                    const isSelectedLocation = selectedStoreLocationId === storeLocation.OrgUnitNumber;
                    const options = this._getPushpinOptions(isSelectedLocation, index, pushpinOptions);
                    const pushpin = new Microsoft.Maps.Pushpin(
                        new Microsoft.Maps.Location(storeLocation.Latitude, storeLocation.Longitude),
                        options
                    );

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        this.handleClickEvent(storeLocation.OrgUnitNumber);
                    });
                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = storeLocation;
                    }
                }
            }

            if (mapLocation) {
                const currentLocation = new Microsoft.Maps.Location(mapLocation.Latitude, mapLocation.Longitude);
                this.map.setView({ center: currentLocation });
            } else {
                // Create a LocationRect from array of pushpins and set the eve-map view.
                this.map.setView({
                    bounds: Microsoft.Maps.LocationRect.fromShapes(pushpins),
                    padding: 80
                });
            }

            let clusterLayer;
            // //Load the Clustering module.
            Microsoft.Maps.loadModule('Microsoft.Maps.Clustering', function() {
                //Create a ClusterLayer and add it to the map.
                clusterLayer = new Microsoft.Maps.ClusterLayer(pushpins);
            });
            if (clusterLayer) {
                this.map.layers.insert(clusterLayer);
            }
            this.map.setOptions({ pushpinAccessible: true });
            // this.map.entities.push(pushpins);
        }
    };

    /**
     * Handle click event.
     * @param orgUnitNumber - Organization unit number.
     */
    private readonly handleClickEvent = async (orgUnitNumber: string | undefined): Promise<void> => {
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager.result;
        const currentSelectedStore = storeSelectorStateManager?.selectedStoreLocationId;
        if (currentSelectedStore === orgUnitNumber) {
            return;
        }
        if (storeSelectorStateManager && storeSelectorStateManager.context && storeSelectorStateManager.context.orgUnitStoreInformation) {
            storeSelectorStateManager.setSelectedStoreLocationId(undefined);
            // const _locations = [...storeSelectorStateManager.context.orgUnitStoreInformation];
            // const channel = _locations.filter(store => {
            //     if (store.OrgUnitLocation?.OrgUnitNumber === orgUnitNumber) {
            //         return true;
            //     }
            //     return false;
            // })[0].OrgUnitLocation?.ChannelId;
            // const getDealerDetailInput = new GetDealerDetailsInput(orgUnitNumber || '');

            // Add conditional to check to see if ContractList exists or not
            if (orgUnitNumber) {
                const dealerDetails = await getDealerDetailsAsync({ callerContext: this.props.context.actionContext }, orgUnitNumber);
                this.selectedDealer = dealerDetails;
                const tempDealerLocations = [...(this.state.newDealerLocations || [])];
                const index = tempDealerLocations.findIndex(loc => loc.OrgUnitLocation?.OrgUnitNumber === orgUnitNumber);
                if (tempDealerLocations[index].OrgUnitLocation) {
                    tempDealerLocations[index].OrgUnitLocation!.ContractList = dealerDetails;
                }

                this.setState(prev => {
                    return {
                        ...prev,
                        newDealerLocations: tempDealerLocations
                    };
                });

                // void getDealerDetails(getDealerDetailInput, this.props.context.actionContext).then(res => {
                //     this.selectedDealer = res.dealerDetails;
                //     console.log(this.selectedDealer);
                //     const tempDealerLocations = [...(this.state.newDealerLocations || [])];
                //     tempDealerLocations.map(location => {
                //         if (location.OrgUnitLocation?.OrgUnitNumber === orgUnitNumber) {
                //             location.OrgUnitLocation = { ...location.OrgUnitLocation, ContractList: res.dealerDetails };
                //         }
                //     });
                //     this.setState(prev => {
                //         return {
                //             ...prev,
                //             newDealerLocations: tempDealerLocations
                //         };
                //     });
                // });
                if (orgUnitNumber) {
                    storeSelectorStateManager?.setSelectedStoreLocationId(orgUnitNumber);
                }
            }
        }
    };

    /**
     * Method to be called on search.
     * @param searchTerm - Search string.
     */
    private readonly _performSearch = async (searchTerm: string): Promise<void> => {
        const {
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!searchTerm || !storeSelectorStateManager) {
            return;
        }

        // eslint-disable-next-line react/no-unused-state -- State is used in the view.
        this.setState({ isSearchInProgress: true });

        await storeSelectorStateManager?.setSelectedStoreLocationId(undefined);
        const location = await storeSelectorStateManager.geoLocate(searchTerm.trim() !== '' ? searchTerm : '0', actionContext);
        if (location) {
            await this._updateLocations(location.latitude, location.longitude);
        } else {
            await this._updateLocations();
        }
        // eslint-disable-next-line react/no-unused-state -- State is used in the view.
        this.setState({ isSearchInProgress: false });
    };

    /**
     * Method to be called on search term changed.
     * @param searchTerm - Search string.
     */
    private readonly _searchTermChanged = async (searchTerm: string): Promise<void> => {
        this.setState({ searchTerm });
    };

    /**
     * Method to update user location.
     * @param latitude - User location.
     * @param longitude - User location.
     * @param radius - Search Criteria Radius.
     * @returns Void promise.
     */
    private readonly _updateLocations = async (latitude?: number, longitude?: number, radius?: number): Promise<void> => {
        const {
            config: { lookupRadius, searchRadiusUnit, defaultLatitudeValue, defaultLongitudeValue },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const distanceUnitValue = searchRadiusUnit === SearchRadiusUnit.km ? 1 : 0;
        const lookupDistanceInMiles = lookupRadius
            ? distanceUnitValue
                ? Math.round(lookupRadius / this.kmToMiValue)
                : lookupRadius
            : this.defaultLookUpRadius;
        // const calRadius = radius || radius === 0 ? radius : lookupRadius || 50;

        let localLatitude = latitude || defaultLatitudeValue;
        let long = longitude || defaultLongitudeValue;

        const lastInlineStoreLocation = storeSelectorStateManager?.lastInlineShownStoreInfo;
        if (lastInlineStoreLocation && !this.state.searchTerm) {
            localLatitude = lastInlineStoreLocation.Latitude ? lastInlineStoreLocation.Latitude : localLatitude;
            long = lastInlineStoreLocation.Longitude ? lastInlineStoreLocation.Longitude : long;
        }
        await storeSelectorStateManager?.setLastInlineStoreInfo(localLatitude, long);
        await this.getOrgUnitLocationByArea(lookupDistanceInMiles, distanceUnitValue, localLatitude, long);
        return;
    };

    /**
     * Get org unit location by area.
     * @param calRadius - Radius.
     * @param distanceUnitValue - Distance unit value.
     * @param latitude - Latitude.
     * @param longitude - Longitude.
     * @returns - Returns a void promise.
     */
    private readonly getOrgUnitLocationByArea = async (
        calRadius: number,
        distanceUnitValue: number,
        latitude?: number,
        longitude?: number
    ): Promise<void> => {
        const {
            context: { actionContext }
        } = this.props;
        // const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(latitude, longitude, calRadius, distanceUnitValue);

        // const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
        const newOrgUnitLocationsInput = new GetDealerLocationsInput(false, latitude || 0, longitude || 0, calRadius);
        const newOrgUnits = await getDealerLocations(newOrgUnitLocationsInput, actionContext);
        // console.log(newOrgUnits);
        if (newOrgUnits && this.state.newDealerLocations) {
            // await this.updateDistances(this.state.newDealerLocations, latitude || 0, longitude || 0);
            // const updatedLocationsList: IFullOrgUnitAvailability[] = this.state.newDealerLocations.map((store, index) => {
            //     const _store: IFullOrgUnitAvailability = _.cloneDeep(store);
            //     if (
            //         _store.OrgUnitLocation &&
            //         _store.OrgUnitAvailability &&
            //         _store.OrgUnitAvailability.OrgUnitLocation &&
            //         this.state.distanceResults.resultsCoords.length > 1
            //     ) {
            //         const travelDistance =
            //             this.props.config.searchRadiusUnit === 'km'
            //                 ? this.state.distanceResults.resultsCoords[index].travelDistance
            //                 : this.state.distanceResults.resultsCoords[index].travelDistance * 0.62137;
            //         _store.OrgUnitLocation.Distance = travelDistance;
            //         _store.OrgUnitAvailability.OrgUnitLocation.Distance = travelDistance;
            //     }
            //     return _store;
            // });
            void this._setOrgUnitStoreInformation(newOrgUnits).then(() => {
                this.setState({ newDealerLocations: [...newOrgUnits] });
            });
        }
        // if (orgUnitLocations && this.state.newDealerLocations) {
        //     // await this.updateDistances(this.state.newDealerLocations, latitude || 0, longitude || 0);
        //     const updatedLocationsList: IFullOrgUnitAvailability[] = this.state.newDealerLocations.map((store, index) => {
        //         const _store: IFullOrgUnitAvailability = _.cloneDeep(store);
        //         if (
        //             _store.OrgUnitLocation &&
        //             _store.OrgUnitAvailability &&
        //             _store.OrgUnitAvailability.OrgUnitLocation &&
        //             this.state.distanceResults.resultsCoords.length > 1
        //         ) {
        //             const travelDistance =
        //                 this.props.config.searchRadiusUnit === 'km'
        //                     ? this.state.distanceResults.resultsCoords[index].travelDistance
        //                     : this.state.distanceResults.resultsCoords[index].travelDistance * 0.62137;
        //             _store.OrgUnitLocation.Distance = travelDistance;
        //             _store.OrgUnitAvailability.OrgUnitLocation.Distance = travelDistance;
        //         }
        //         return _store;
        //     });
        //     await this._setOrgUnitStoreInformation(updatedLocationsList).then(() =>
        //         this.setState({ newDealerLocations: [...updatedLocationsList] })
        //     );
        // }
    };

    /**
     * Method to set store info.
     * @param orgUnitStoreInformation - Store Info Object.
     * @returns Void promise.
     */
    private readonly _setOrgUnitStoreInformation = async (orgUnitStoreInformation: IFullOrgUnitAvailability[]): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            telemetry
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        const context = storeSelectorStateManager.context;
        const moduleId: string = context?.id ? context.id : this.props.id;
        let input: IStoreSelectionStateContext = {
            /**
             * On Location selected.
             * @returns - Returns promise.
             */
            onLocationSelected: async () => {
                return Promise.resolve();
            }
        };

        if (storeSelectorStateManager.context) {
            input = storeSelectorStateManager.context;
        }

        // const storeLocationList = this._sortStores(orgUnitStoreInformation);
        input = {
            ...input,
            id: moduleId,
            showMapViewLink: context?.showMapViewLink,
            parentElementRef: context?.parentElementRef,
            orgUnitStoreInformation: orgUnitStoreInformation,
            product: context?.product
        };

        await storeSelectorStateManager.setOrgUnitStoreInformation(input).catch((error: Error) => {
            if (telemetry) {
                telemetry.error(error.message);
                telemetry.debug('Unable to Update store location state');
            }
        });
    };

    // /**
    //  * Method will sort all stores.
    //  * @param stores -Stores List.
    //  * @param isPreferredStoreEnabled -Module config value.
    //  * @param preferredStoreLocationId -Store Id.
    //  * @returns List of Stores.
    //  */
    // eslint-disable-next-line class-methods-use-this -- No need of introducing new variable.
    // private _sortStores(stores: IFullOrgUnitAvailability[]): IFullOrgUnitAvailability[] {
    //     const {
    //         config: { lookupRadius }
    //     } = this.props;
    //     const sortedStores = [...stores];
    //     if (sortedStores.filter(store => store.OrgUnitLocation?.Distance).length > 0) {
    //         return [
    //             ...sortedStores
    //                 .sort((a, b) => a.OrgUnitLocation!.Distance! - b.OrgUnitLocation!.Distance!)
    //                 .filter(store => store.OrgUnitLocation!.Distance! <= lookupRadius)
    //         ];
    //     }
    //     return [...sortedStores];
    // }

    // private async updateDistances(storeLocationList: IFullOrgUnitAvailability[], latitude: number, longitude: number) {
    // const {
    //     distanceResults: {
    //         originCoords: { latitude: originLat, longitude: originLon }
    //     }
    // } = this.state;
    // if (latitude !== originLat && longitude !== originLon) {
    //     let dealerLocationCoordsString: string = '&destinations=';
    //     storeLocationList.map((store, index) => {
    //         dealerLocationCoordsString += `${store.OrgUnitLocation?.Latitude},${store.OrgUnitLocation?.Longitude}`;
    //         if (index < storeLocationList.length - 1) {
    //             dealerLocationCoordsString += ';';
    //         }
    //     });
    //     const currentLocation = `?origins=${latitude},${longitude}`;
    //     // const apiKey = this.props.context.actionContext.requestContext.channel?.BingMapsApiKey;
    //     const modeAndApiString = `&travelMode=driving&key=${apiKey}`;
    //     // const modeAndApiString = `&travelMode=driving&key=${this.state.mapSessionId ? this.state.mapSessionId : apiKey}`;
    //     // TODO: Figure out how to use the session id, does it need to be a separate id for the distance matrix?
    //     // currently getting authorization errors when using it.

    //     const distanceMatrixUrl = `https://dev.virtualearth.net/REST/v1/Routes/DistanceMatrix${currentLocation}${dealerLocationCoordsString}${modeAndApiString}`;

    //     const resultsCoords = await this.getDistances(distanceMatrixUrl).then(results => {
    //         this.setState(prev => {
    //             return {
    //                 ...prev,
    //                 distanceResults: {
    //                     originCoords: { latitude: latitude || 0, longitude: longitude || 0 },
    //                     resultsCoords: [...results]
    //                 }
    //             };
    //         });
    //     });
    // }

    //     console.log('Updating distances.');
    // }

    // private async getDistances(url: string) {
    //     try {
    //         const response = await fetch(url);
    //         const data = await response.json();
    //         return data.resourceSets[0].resources[0].results;
    //     } catch (error) {
    //         console.log(error);
    //     }
    // }

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinOptions = (isSelectedLocation: boolean, index: number, pushpinOptions?: IPushpinOptionsData) => {
        const text = pushpinOptions?.showIndex ? (index + 1).toString() : undefined;

        const size = pushpinOptions?.size || 1;
        const color = isSelectedLocation ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text);

        return {
            // Fallback if icon doesn't render properly
            color: isSelectedLocation ? pushpinOptions?.selectionColor : pushpinOptions?.color,
            icon
        };
    };

    private readonly _getSvgIcon = (size: number, color?: string, text?: string) => {
        const baseValue: number = 32;
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${baseValue * size}" height="${baseValue * size}" viewBox="0 0 365 560">
                    <path fill="${color}" d="M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z" />
                    ${text ? `<text x="185" y="280" style="font-size:220px;fill:#ffffff;" text-anchor="middle">${text}</text>` : ''}
                </svg>`;
    };
}

export default Map;
